/**
 * Platform Health section (Task 32c.1).
 *
 * Surface the cross-org snapshot data shipped by 32b's
 * /api/platform/overview endpoint. The endpoint reads from the
 * `platform_snapshots` table that the platform-sync cron writes
 * every 15 min (gated by PLATFORM_SYNC_ENABLED).
 *
 * Behaviour:
 *   - Initial fetch on mount + repoll every 60s
 *   - On poll failure, freeze the previously-displayed numbers and
 *     mark them "Stale" so the operator still has signal — never
 *     show a blank card or "—"s after a successful first load
 *   - Sync health colour: green if last snapshot <30 min, yellow
 *     <2h, red older. None / unknown rendered as muted grey
 */
import { useEffect, useRef, useState } from 'react';
import {
  ServerStackIcon,
  UsersIcon,
  FolderIcon,
  SignalIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { LoadingSkeleton, ErrorState } from '@repro/ui';
import { api } from '../utils/api';

interface PlatformOverview {
  totalOrgs: number;
  totalUsers: number;
  totalProjects: number;
  totalProgramsOnline: number;
  snapshotAt: string | null;
}

const POLL_INTERVAL_MS = 60_000;

type SyncHealth = 'fresh' | 'stale' | 'old' | 'unknown';

function syncHealthFromSnapshot(snapshotAt: string | null): SyncHealth {
  if (!snapshotAt) return 'unknown';
  const ageMs = Date.now() - new Date(snapshotAt).getTime();
  if (Number.isNaN(ageMs)) return 'unknown';
  if (ageMs < 30 * 60_000) return 'fresh';
  if (ageMs < 2 * 60 * 60_000) return 'stale';
  return 'old';
}

function formatTimeAgo(snapshotAt: string | null): string {
  if (!snapshotAt) return 'never';
  const ageMs = Date.now() - new Date(snapshotAt).getTime();
  if (Number.isNaN(ageMs) || ageMs < 0) return 'unknown';
  const sec = Math.floor(ageMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

export function PlatformHealthSection() {
  const [overview, setOverview] = useState<PlatformOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Stale = had a successful load before, but the last poll failed
  const [stale, setStale] = useState(false);
  const cancelledRef = useRef(false);
  const overviewRef = useRef<PlatformOverview | null>(null);

  useEffect(() => {
    cancelledRef.current = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      try {
        const data = await api.getPlatformOverview();
        if (cancelledRef.current) return;
        overviewRef.current = data;
        setOverview(data);
        setError(null);
        setStale(false);
      } catch (err) {
        if (cancelledRef.current) return;
        const msg = err instanceof Error ? err.message : 'Unknown error';
        // First-load failure -> error UI; subsequent poll failure -> Stale badge
        if (overviewRef.current === null) {
          setError(msg);
        } else {
          setStale(true);
        }
      }
      if (cancelledRef.current) return;
      setLoading(false);
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => {
      cancelledRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (loading && overview === null) {
    return (
      <Section title="Platform Health">
        <LoadingSkeleton rows={3} />
      </Section>
    );
  }

  if (error && overview === null) {
    return (
      <Section title="Platform Health">
        <ErrorState
          title="Couldn't load platform health"
          body={error}
          onRetry={() => window.location.reload()}
        />
      </Section>
    );
  }

  if (!overview) return null;

  const health = syncHealthFromSnapshot(overview.snapshotAt);

  return (
    <Section title="Platform Health" stale={stale}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat
          icon={<ServerStackIcon className="h-5 w-5" />}
          label="Organisations"
          value={overview.totalOrgs}
        />
        <Stat
          icon={<UsersIcon className="h-5 w-5" />}
          label="Active users"
          value={overview.totalUsers}
        />
        <Stat
          icon={<FolderIcon className="h-5 w-5" />}
          label="Projects"
          value={overview.totalProjects}
        />
        <Stat
          icon={<SignalIcon className="h-5 w-5" />}
          label="Programs online"
          value={overview.totalProgramsOnline}
          highlight
        />
      </div>
      <div className="mt-4 flex items-center justify-between text-xs text-base-content/60">
        <div className="flex items-center gap-1.5">
          <ClockIcon className="h-3.5 w-3.5" />
          <span>Last sync: {formatTimeAgo(overview.snapshotAt)}</span>
        </div>
        <SyncHealthBadge health={health} />
      </div>
    </Section>
  );
}

function Section({
  title,
  stale,
  children,
}: {
  title: string;
  stale?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-base-100 shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-base-content">{title}</h2>
        {stale && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Stale
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div className="rounded-lg border border-base-300 bg-base-200/40 p-3">
      <div className="flex items-center gap-2 text-base-content/70 text-xs font-medium">
        {icon}
        <span>{label}</span>
      </div>
      <div
        className={`text-2xl font-bold mt-1 ${highlight ? 'text-success' : 'text-base-content'}`}
      >
        {value.toLocaleString('en-GB')}
      </div>
    </div>
  );
}

function SyncHealthBadge({ health }: { health: SyncHealth }) {
  const map: Record<SyncHealth, { label: string; cls: string; dot: string }> = {
    fresh: {
      label: 'Sync healthy',
      cls: 'bg-green-100 text-green-800',
      dot: 'bg-green-500',
    },
    stale: {
      label: 'Sync delayed',
      cls: 'bg-yellow-100 text-yellow-800',
      dot: 'bg-yellow-500',
    },
    old: {
      label: 'Sync stuck',
      cls: 'bg-red-100 text-red-800',
      dot: 'bg-red-500',
    },
    unknown: {
      label: 'No snapshots yet',
      cls: 'bg-base-200 text-base-content/70',
      dot: 'bg-base-content/30',
    },
  };
  const { label, cls, dot } = map[health];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}
